import { component$, useTask$ } from "@builder.io/qwik";
import { LuAward } from "@qwikest/icons/lucide";
import type {
  CoachSelect,
  OfferBenefitSelect,
  OfferSelect,
  OfferTagSelect,
} from "../../drizzle/schema";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { MarkdownContent } from "./markdown-content";
import { Feature, hasFeature } from "~/lib/has-feature";
import { PriceDisplay } from "./ui/price-display";
import { Form, useNavigate } from "@builder.io/qwik-city";
import { useCreateCheckout } from "~/routes/[username]/(profile)/layout";

type Offer = OfferSelect & {
  tags: OfferTagSelect[];
  benefits: OfferBenefitSelect[];
};

export const OfferCard = component$<{
  offer: Offer;
  coach: CoachSelect;
  ctaButtonText: string;
}>(({ offer, coach, ctaButtonText }) => {
  const createCheckout = useCreateCheckout();
  const nav = useNavigate();

  // Handle successful checkout session creation
  useTask$(({ track }) => {
    const value = track(() => createCheckout.value);
    if (value?.checkoutUrl) {
      nav(value.checkoutUrl);
    }
  });

  const hasContent =
    offer.tags.length > 0 ||
    offer.headline ||
    offer.description ||
    offer.benefits.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <div class="mt-8 flex flex-col items-center gap-8">
          {offer.tags.length > 0 && (
            <div class="flex items-center justify-center gap-2">
              {offer.tags.map((t) => (
                <Badge key={t.id} variant="outline-secondary">
                  {t.title}
                </Badge>
              ))}
            </div>
          )}
          <div class="flex flex-col items-center gap-4">
            {offer.headline && (
              <h2 class="text-primary font-heading text-center text-4xl leading-none">
                {offer.headline}
              </h2>
            )}
            {offer.description && (
              <div class="text-secondary text-center text-lg">
                <MarkdownContent content={offer.description} />
              </div>
            )}
          </div>
          {offer.benefits.length > 0 && (
            <div class="text-secondary flex flex-col flex-wrap items-center justify-center gap-4 text-sm sm:flex-row">
              {offer.benefits.map((benefit) => (
                <div key={benefit.id} class="flex items-center gap-2">
                  <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-100 text-purple-700">
                    <LuAward />
                  </div>
                  {benefit.title}
                </div>
              ))}
            </div>
          )}
          {hasFeature(coach, Feature.OfferPage) ? (
            <a href={`/${coach.username}/offer`}>
              <Button variant="fancy" size="lg">
                Read more
              </Button>
            </a>
          ) : offer.directSaleEnabled ? (
            <>
              <div class="flex flex-col items-center gap-2">
                <PriceDisplay
                  price={offer.price!}
                  currency={offer.currency!}
                  originalPrice={offer.originalPrice}
                  size="lg"
                />
                {/* Show error message if checkout creation failed */}
                {createCheckout.value?.error && (
                  <div class="text-sm text-red-600">
                    {createCheckout.value.error}
                  </div>
                )}
                <Form action={createCheckout}>
                  <input type="hidden" name="offerId" value={offer.id} />
                  <Button
                    type="submit"
                    variant="fancy"
                    size="lg"
                    disabled={createCheckout.isRunning}
                  >
                    Buy Now
                  </Button>
                </Form>
              </div>
            </>
          ) : (
            coach.ghlCalendarId && (
              <a href={`/${coach.username}/book`}>
                <Button variant="fancy" size="lg">
                  {ctaButtonText}
                </Button>
              </a>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
});
